import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Upload, X } from 'lucide-react';

export default function JntImport() {
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(()=>{ load(); },[]);

  async function load() {
    try { const r=await api.get('/billing/jnt/reports'); setReports(r.data||[]); } catch {}
  }

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post('/billing/jnt/import', form, { headers:{'Content-Type':'multipart/form-data'} });
      setResult(r.data);
      toast.success(`Imported! ${r.data.matched}/${r.data.total_items} matched`);
      load();
    } catch(e) { toast.error(e.response?.data?.detail||'Import failed'); }
    finally { setUploading(false); if(fileRef.current) fileRef.current.value=''; }
  };

  const loadReport = async (id) => {
    try { const r=await api.get(`/billing/jnt/reports/${id}`); setSelected(r.data); } catch { toast.error('Failed'); }
  };

  const manualMatch = async (reportId, itemId, billNumber) => {
    const r = await api.get('/billing/bills', { params:{search:billNumber,per_page:5} });
    const bills = r.data.bills||[];
    if (!bills.length) return toast.error('Bill not found');
    const bill = bills[0];
    if (!window.confirm(`Match to ${bill.bill_number} — ${bill.customer_name}?`)) return;
    try {
      await api.post(`/billing/jnt/reports/${reportId}/items/${itemId}/match`, { bill_id: bill.id });
      toast.success('Matched!');
      loadReport(reportId);
    } catch { toast.error('Failed'); }
  };

  return (
    <div style={{ padding:24, minHeight:'100vh', background:'#f8fafc' }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'#111827' }}>📊 J&T COD Reports</h1>
        <p style={{ margin:'2px 0 0', fontSize:13, color:'#6b7280' }}>Import daily J&T XLSX reports to auto-match and confirm payments</p>
      </div>

      {/* Upload */}
      <div style={{ background:'#fff', border:'2px dashed #e5e7eb', borderRadius:12, padding:32, textAlign:'center', marginBottom:20, cursor:'pointer' }}
        onClick={()=>fileRef.current?.click()}>
        <Upload size={32} style={{ color:'#9ca3af', marginBottom:12 }} />
        <div style={{ fontSize:14, fontWeight:600, color:'#374151', marginBottom:4 }}>Upload J&T Daily Report</div>
        <div style={{ fontSize:12, color:'#9ca3af' }}>Click to select .xlsx file</div>
        <input ref={fileRef} type="file" accept=".xlsx" onChange={upload} style={{ display:'none' }} />
        {uploading&&<div style={{ marginTop:12, color:'#7c6af5', fontWeight:600 }}>Importing...</div>}
      </div>

      {/* Import result */}
      {result&&(
        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:16, marginBottom:20 }}>
          <div style={{ fontWeight:700, color:'#16a34a', marginBottom:8 }}>✅ Import Complete</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, fontSize:13 }}>
            <div><div style={{ color:'#6b7280' }}>Total Items</div><div style={{ fontWeight:700, fontSize:16 }}>{result.total_items}</div></div>
            <div><div style={{ color:'#6b7280' }}>Matched</div><div style={{ fontWeight:700, fontSize:16, color:'#16a34a' }}>{result.matched}</div></div>
            <div><div style={{ color:'#6b7280' }}>Unmatched</div><div style={{ fontWeight:700, fontSize:16, color:'#f59e0b' }}>{result.unmatched}</div></div>
            <div><div style={{ color:'#6b7280' }}>Net Payout</div><div style={{ fontWeight:700, fontSize:16, color:'#7c6af5' }}>${result.net_payout_usd?.toFixed(2)}</div></div>
          </div>
          <div style={{ fontSize:12, color:'#6b7280', marginTop:8 }}>
            Collected: {parseFloat(result.total_collected_khr||0).toLocaleString()}៛ · Fee: {parseFloat(result.total_fee_khr||0).toLocaleString()}៛ · Net: {parseFloat(result.net_payout_khr||0).toLocaleString()}៛
          </div>
        </div>
      )}

      {/* Reports history */}
      <div style={{ background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', overflow:'hidden', marginBottom:selected?0:0 }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
              {['Date','Account','Total Collected','Fee','Net Payout USD','Items','Matched','Action'].map(h=>(
                <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reports.length===0&&<tr><td colSpan={8} style={{ padding:32, textAlign:'center', color:'#9ca3af' }}>No reports imported yet</td></tr>}
            {reports.map(r=>(
              <tr key={r.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                <td style={{ padding:'8px 12px', fontWeight:600, color:'#374151' }}>{r.report_date}</td>
                <td style={{ padding:'8px 12px', color:'#6b7280' }}>{r.account_id||'—'}</td>
                <td style={{ padding:'8px 12px', color:'#374151' }}>{parseFloat(r.total_collected_khr||0).toLocaleString()}៛</td>
                <td style={{ padding:'8px 12px', color:'#dc2626' }}>{parseFloat(r.total_fee_khr||0).toLocaleString()}៛</td>
                <td style={{ padding:'8px 12px', fontWeight:700, color:'#16a34a' }}>${parseFloat(r.net_payout_usd||0).toFixed(2)}</td>
                <td style={{ padding:'8px 12px', textAlign:'center' }}>{r.total_items}</td>
                <td style={{ padding:'8px 12px', textAlign:'center' }}>
                  <span style={{ fontWeight:600, color: r.matched_items===r.total_items?'#16a34a':'#f59e0b' }}>{r.matched_items}/{r.total_items}</span>
                </td>
                <td style={{ padding:'8px 12px' }}>
                  <button onClick={()=>loadReport(r.id)} style={{ background:'#eff6ff', color:'#2563eb', border:'none', borderRadius:6, padding:'4px 10px', fontSize:12, cursor:'pointer' }}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Report detail */}
      {selected&&(
        <div style={{ marginTop:16, background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid #e5e7eb', background:'#f9fafb' }}>
            <div style={{ fontWeight:700, color:'#111827' }}>Report {selected.report_date} — {selected.matched_items}/{selected.total_items} matched</div>
            <button onClick={()=>setSelected(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}><X size={16}/></button>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:'#f9fafb' }}>
                {['J&T Tracking','Bill','Customer','Collected KHR','Fee KHR','Net KHR','Received','Status'].map(h=>(
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selected.items?.map(item=>(
                <tr key={item.id} style={{ borderBottom:'1px solid #f3f4f6', background: item.matched?'':'#fffbeb' }}>
                  <td style={{ padding:'7px 12px', fontFamily:'monospace', fontSize:11, color:'#374151' }}>{item.jnt_tracking_no}</td>
                  <td style={{ padding:'7px 12px' }}>
                    {item.matched
                      ? <span style={{ fontFamily:'monospace', color:'#7c6af5', fontWeight:700, fontSize:11 }}>{item.bill_number}</span>
                      : <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                          <span style={{ color:'#f59e0b', fontSize:11 }}>Unmatched</span>
                          <input placeholder="Bill no..." style={{ background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:4, padding:'2px 6px', fontSize:11, width:120, outline:'none' }}
                            onKeyDown={e=>{ if(e.key==='Enter') manualMatch(selected.id, item.id, e.target.value); }} />
                        </div>}
                  </td>
                  <td style={{ padding:'7px 12px', color:'#374151' }}>{item.matched?'—':'—'}</td>
                  <td style={{ padding:'7px 12px', color:'#374151' }}>{parseFloat(item.amount_khr||0).toLocaleString()}៛</td>
                  <td style={{ padding:'7px 12px', color:'#dc2626' }}>{parseFloat(item.fee_khr||0).toLocaleString()}៛</td>
                  <td style={{ padding:'7px 12px', fontWeight:700, color:'#16a34a' }}>{parseFloat(item.net_khr||0).toLocaleString()}៛</td>
                  <td style={{ padding:'7px 12px', color:'#9ca3af' }}>{item.received_date||'—'}</td>
                  <td style={{ padding:'7px 12px' }}>
                    {item.matched
                      ? <span style={{ fontSize:11, background:'#dcfce7', color:'#16a34a', padding:'2px 8px', borderRadius:8, fontWeight:600 }}>✅ Matched</span>
                      : <span style={{ fontSize:11, background:'#fef3c7', color:'#f59e0b', padding:'2px 8px', borderRadius:8, fontWeight:600 }}>⚠️ Pending</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
